import * as React from 'react';
import { useMemo } from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import {
    PriceCalculatorBox,
    PriceRow,
    PriceComparisonRow,
    PriceComparisonSection,
    PriceBreakdownColumn,
    SectionHeader,
    ChangeIndicator
} from './styled';
import { SubscriptionPlanDefinition, SubscriptionAddonTier, SubscriptionAddonDefinition, SubscriptionDiscount } from '../../models';
import { formatCurrency, getAddonTierPrice } from './utils';

export interface PriceCalculatorProps {
    selectedPlan: SubscriptionPlanDefinition;
    selectedUsersTier: SubscriptionAddonTier | null;
    selectedSmsTier: SubscriptionAddonTier | null;
    selectedWhatsAppTier: SubscriptionAddonTier | null;
    selectedFeatureAddons: Record<string, SubscriptionAddonTier | boolean>;
    addons: Record<string, SubscriptionAddonDefinition>;
    billingPeriod: 'monthly' | 'yearly';
    currentMonthlyTotal?: number;
    discounts?: SubscriptionDiscount[];
    // Current subscription data for comparison
    currentPlan?: SubscriptionPlanDefinition;
    currentUsersTier?: SubscriptionAddonTier | null;
    currentSmsTier?: SubscriptionAddonTier | null;
    currentWhatsAppTier?: SubscriptionAddonTier | null;
    currentFeatureAddons?: Record<string, boolean>;
    onHasChangesChange?: (hasChanges: boolean) => void;
}

const PriceCalculator: React.FC<PriceCalculatorProps> = ({
    selectedPlan,
    selectedUsersTier,
    selectedSmsTier,
    selectedWhatsAppTier,
    selectedFeatureAddons,
    addons,
    billingPeriod,
    currentMonthlyTotal,
    discounts,
    currentPlan,
    currentUsersTier,
    currentSmsTier,
    currentWhatsAppTier,
    currentFeatureAddons,
    onHasChangesChange,
}) => {
    const t = useTranslations();

    const resolveAddon = (key: string): SubscriptionAddonDefinition | undefined => {
        return addons[key] || addons[key.toLowerCase()] || addons[key.toUpperCase()];
    };

    const getSelectedAddonPrice = (addon: SubscriptionAddonTier | boolean, key: string) => {
        const definition = resolveAddon(key);
        if (typeof addon === 'object') {
            return getAddonTierPrice(addon, billingPeriod);
        }
        if (addon === true && definition?.tiers?.[0]) {
            return getAddonTierPrice(definition.tiers[0], billingPeriod);
        }
        return 0;
    };

    // Detect if there are any changes from current subscription
    const hasChanges = useMemo(() => {
        if (!currentPlan) return false;

        // Check plan change
        if (selectedPlan.name !== currentPlan.name) return true;

        // Check users tier change
        if (selectedUsersTier?.quantity !== currentUsersTier?.quantity) return true;

        // Check SMS tier change
        if (selectedSmsTier?.quantity !== currentSmsTier?.quantity) return true;

        // Check WhatsApp tier change
        if (selectedWhatsAppTier?.quantity !== currentWhatsAppTier?.quantity) return true;

        // Check feature addons changes
        if (currentFeatureAddons) {
            const selectedFeatureKeys = Object.keys(selectedFeatureAddons).filter(k => selectedFeatureAddons[k]);
            const currentFeatureKeys = Object.keys(currentFeatureAddons).filter(k => currentFeatureAddons[k]);

            if (selectedFeatureKeys.length !== currentFeatureKeys.length) return true;
            if (!selectedFeatureKeys.every(k => currentFeatureKeys.includes(k))) return true;
        }

        return false;
    }, [selectedPlan, currentPlan, selectedUsersTier, currentUsersTier, selectedSmsTier, currentSmsTier,
        selectedWhatsAppTier, currentWhatsAppTier,
        selectedFeatureAddons, currentFeatureAddons]);

    React.useEffect(() => onHasChangesChange && onHasChangesChange(hasChanges), [hasChanges])

    // Get plan pricing from server data
    const basePlanPrice = billingPeriod === 'yearly'
        ? selectedPlan.pricePerMonthBilledAnnually
        : selectedPlan.pricePerMonthBilledMonthly;

    const currentBasePlanPrice = currentPlan && billingPeriod === 'yearly'
        ? currentPlan.pricePerMonthBilledAnnually
        : currentPlan?.pricePerMonthBilledMonthly;

    // Calculate total discount based on billing period
    const totalDiscount = useMemo(() => {
        if (!discounts || discounts.length === 0) return 0;

        return discounts.reduce((sum, discount) => {
            const discountValue = billingPeriod === 'yearly'
                ? discount.yearlyDiscountValue
                : discount.monthlyDiscountValue;
            return sum + discountValue;
        }, 0);
    }, [discounts, billingPeriod]);

    const monthlyTotal = useMemo(() => {
        let total = basePlanPrice;

        // Add users tier cost
        if (selectedUsersTier) {
            total += selectedUsersTier.pricePerMonth;
        }

        // Add SMS tier cost
        if (selectedSmsTier) {
            total += selectedSmsTier.pricePerMonth;
        }

        // Add WhatsApp tier cost
        if (selectedWhatsAppTier) {
            total += selectedWhatsAppTier.pricePerMonth;
        }

        // Add feature addons cost
        Object.entries(selectedFeatureAddons).forEach(([type, addon]) => {
            if (!addon) return;
            const price = getSelectedAddonPrice(addon, type);
            if (price) total += price;
        });

        // Apply discounts
        total -= totalDiscount;

        return Math.max(0, total); // Ensure total doesn't go negative
    }, [basePlanPrice, selectedUsersTier, selectedSmsTier, selectedWhatsAppTier, selectedFeatureAddons, totalDiscount]);
    const hasCurrentMonthlyTotal = typeof currentMonthlyTotal === 'number';
    const difference = hasCurrentMonthlyTotal ? monthlyTotal - currentMonthlyTotal : 0;
    const yearlyTotal = monthlyTotal * 12;

    // VAT calculation (23%)
    const VAT_RATE = 0.23; // TODO - get this from pack
    const yearlyTotalWithVAT = yearlyTotal * (1 + VAT_RATE);

    // Helper to render change indicator
    const renderChangeIndicator = (changeType: 'added' | 'removed' | 'modified' | 'unchanged' | 'discount') => {
        const symbols = { added: '+', removed: '−', modified: '→', unchanged: '=', discount: '%' };
        return <ChangeIndicator className={changeType}>{symbols[changeType]}</ChangeIndicator>;
    };

    // Helper to determine change type
    const getChangeType = (
        current: any,
        selected: any,
        compareFn?: (a: any, b: any) => boolean
    ): 'added' | 'removed' | 'modified' | 'unchanged' => {
        const isEqual = compareFn ? compareFn(current, selected) : current === selected;

        if (!current && selected) return 'added';
        if (current && !selected) return 'removed';
        if (!isEqual) return 'modified';
        return 'unchanged';
    };

    return (
        <PriceCalculatorBox>
            {/* Left: Breakdown */}
            {!hasChanges ? (
                // Single column when no changes
                <div className="price-breakdown">
                    <PriceRow>
                        <span>
                            {selectedPlan.name} {t(TK.subscription)}
                        </span>
                        <span>{formatCurrency(basePlanPrice)} / {t(TK.month)}</span>
                    </PriceRow>

                    {selectedUsersTier && (
                        <PriceRow>
                            <span>
                                {t(TK.users)} (+{selectedUsersTier.quantity})
                            </span>
                            <span>{formatCurrency(selectedUsersTier.pricePerMonth)} / {t(TK.month)}</span>
                        </PriceRow>
                    )}

                    {selectedSmsTier && (
                        <PriceRow>
                            <span>{t(TK.sms)} ({selectedSmsTier.quantity})</span>
                            <span>{formatCurrency(selectedSmsTier.pricePerMonth)} / {t(TK.month)}</span>
                        </PriceRow>
                    )}

                    {selectedWhatsAppTier && (
                        <PriceRow>
                            <span>WhatsApp ({selectedWhatsAppTier.quantity})</span>
                            <span>{formatCurrency(selectedWhatsAppTier.pricePerMonth)} / {t(TK.month)}</span>
                        </PriceRow>
                    )}

                    {Object.entries(selectedFeatureAddons).map(([type, addon]) => {
                        if (!addon) return null;
                        const definition = resolveAddon(type);
                        const price = getSelectedAddonPrice(addon, type);
                        if (!price) return null;

                        return (
                            <PriceRow key={type}>
                                <span>{definition?.name ?? type}</span>
                                <span>{formatCurrency(price)} / {t(TK.month)}</span>
                            </PriceRow>
                        );
                    })}

                    {totalDiscount > 0 && discounts && discounts.map((discount, index) => (
                        <PriceRow key={`discount-${index}`} className="discount">
                            <span style={{ color: 'var(--success-color)' }}>
                                {renderChangeIndicator('discount')}
                                {discount.name}
                            </span>
                            <span style={{ color: 'var(--success-color)' }}>
                                -{formatCurrency(billingPeriod === 'yearly' ? discount.yearlyDiscountValue : discount.monthlyDiscountValue)} / {t(TK.month)}
                            </span>
                        </PriceRow>
                    ))}
                </div>
            ) : (
                // Two columns when changes exist
                <PriceComparisonSection>
                    {/* Current Subscription */}
                    <PriceBreakdownColumn className="current">
                        <SectionHeader>{t(TK.current)}</SectionHeader>
                        {currentPlan && currentBasePlanPrice !== undefined && (
                            <PriceRow>
                                <span>
                                    {currentPlan.name} {t(TK.subscription)}
                                </span>
                                <span>{formatCurrency(currentBasePlanPrice)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {currentUsersTier && (
                            <PriceRow>
                                <span>
                                    {t(TK.users)} (+{currentUsersTier.quantity})
                                </span>
                                <span>{formatCurrency(currentUsersTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {currentSmsTier && (
                            <PriceRow>
                                <span>{t(TK.sms)} ({currentSmsTier.quantity})</span>
                                <span>{formatCurrency(currentSmsTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {currentWhatsAppTier && (
                            <PriceRow>
                                <span>WhatsApp ({currentWhatsAppTier.quantity})</span>
                                <span>{formatCurrency(currentWhatsAppTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {currentFeatureAddons && Object.entries(currentFeatureAddons).map(([type, enabled]) => {
                            if (!enabled) return null;
                            const price = getSelectedAddonPrice(enabled, type);
                            const definition = resolveAddon(type);
                            if (!price) return null;

                            return (
                                <PriceRow key={type}>
                                    <span>{definition?.name ?? type}</span>
                                    <span>{formatCurrency(price)} / {t(TK.month)}</span>
                                </PriceRow>
                            );
                        })}
                        {/* Discounts */}
                        {totalDiscount > 0 && discounts && discounts.map((discount, index) => (
                            <PriceRow key={`discount-${index}`} className="discount">
                                <span style={{ color: 'var(--success-color)' }}>
                                    {renderChangeIndicator('discount')}
                                    {discount.name}
                                </span>
                                <span style={{ color: 'var(--success-color)' }}>
                                    -{formatCurrency(billingPeriod === 'yearly' ? discount.yearlyDiscountValue : discount.monthlyDiscountValue)} / {t(TK.month)}
                                </span>
                            </PriceRow>
                        ))}
                    </PriceBreakdownColumn>

                    {/* After Changes */}
                    <PriceBreakdownColumn className="changes">
                        <SectionHeader className="changes-header">{t(TK.afterChanges)}</SectionHeader>

                        {/* Plan */}
                        <PriceRow>
                            <span>
                                {renderChangeIndicator(getChangeType(currentPlan?.name, selectedPlan.name))}
                                {selectedPlan.name} {t(TK.subscription)}
                            </span>
                            <span>{formatCurrency(basePlanPrice)} / {t(TK.month)}</span>
                        </PriceRow>

                        {/* Users */}
                        {selectedUsersTier && (
                            <PriceRow>
                                <span>
                                    {renderChangeIndicator(getChangeType(
                                        currentUsersTier?.quantity,
                                        selectedUsersTier.quantity
                                    ))}
                                    {t(TK.users)} (+{selectedUsersTier.quantity})
                                </span>
                                <span>{formatCurrency(selectedUsersTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {/* SMS */}
                        {selectedSmsTier && (
                            <PriceRow>
                                <span>
                                    {renderChangeIndicator(getChangeType(
                                        currentSmsTier?.quantity,
                                        selectedSmsTier.quantity
                                    ))}
                                    {t(TK.sms)} ({selectedSmsTier.quantity})
                                </span>
                                <span>{formatCurrency(selectedSmsTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {/* WhatsApp */}
                        {selectedWhatsAppTier && (
                            <PriceRow>
                                <span>
                                    {renderChangeIndicator(getChangeType(
                                        currentWhatsAppTier?.quantity,
                                        selectedWhatsAppTier.quantity
                                    ))}
                                    WhatsApp ({selectedWhatsAppTier.quantity})
                                </span>
                                <span>{formatCurrency(selectedWhatsAppTier.pricePerMonth)} / {t(TK.month)}</span>
                            </PriceRow>
                        )}

                        {/* Feature Addons */}
                        {Object.entries(selectedFeatureAddons).map(([type, addon]) => {
                            if (!addon) return null;
                            const definition = resolveAddon(type);
                            const price = getSelectedAddonPrice(addon, type);
                            if (!price) return null;

                            const wasEnabled = currentFeatureAddons?.[type] ?? false;
                            const changeType = getChangeType(wasEnabled, !!addon);

                                    return (
                                        <PriceRow key={type}>
                                            <span>
                                                {renderChangeIndicator(changeType)}
                                                {definition?.name ?? type}
                                            </span>
                                            <span>{formatCurrency(price)} / {t(TK.month)}</span>
                                        </PriceRow>
                                    );
                                })}

                        {/* Discounts */}
                        {totalDiscount > 0 && discounts && discounts.map((discount, index) => (
                            <PriceRow key={`discount-${index}`} className="discount">
                                <span style={{ color: 'var(--success-color)' }}>
                                    {renderChangeIndicator('discount')}
                                    {discount.name}
                                </span>
                                <span style={{ color: 'var(--success-color)' }}>
                                    -{formatCurrency(billingPeriod === 'yearly' ? discount.yearlyDiscountValue : discount.monthlyDiscountValue)} / {t(TK.month)}
                                </span>
                            </PriceRow>
                        ))}
                    </PriceBreakdownColumn>
                </PriceComparisonSection>
            )}

            {/* Right: Total & CTA */}
            <div className="price-total-section">

                <div className="price-main">
                    {formatCurrency(monthlyTotal)}
                    <span className="per-month"> / {t(TK.month)}</span>
                </div>

                <div className="price-secondary">
                    {t(TK.yearly)}: {formatCurrency(yearlyTotal)} <br />
                    {t(TK.withVAT)}: {formatCurrency(yearlyTotalWithVAT)}
                </div>

                {hasChanges && (
                    <>
                        <PriceComparisonRow>
                            <span>{t(TK.currentlyPaying)}:</span>
                            <span>{formatCurrency(currentMonthlyTotal!)} / {t(TK.month)}</span>
                        </PriceComparisonRow>
                        <PriceComparisonRow className={difference > 0 ? 'increase' : difference < 0 ? 'decrease' : ''}>
                            <span>{t(TK.difference)}:</span>
                            <span>{difference > 0 ? '+' : ''}{formatCurrency(difference)} / {t(TK.month)}</span>
                        </PriceComparisonRow>
                    </>
                )}
            </div>
        </PriceCalculatorBox>
    );
};

export default PriceCalculator;
